import { useAtomValue } from 'jotai';
import { type ReactNode, useMemo } from 'react';

import { prefixesOf, toSegments } from '@frontend/utils/modePath';

import {
  spotlightActionsAtom,
  spotlightGroupsAtom,
  spotlightModeDataAtom,
  spotlightModesAtom,
  spotlightModeStackAtom,
  spotlightQueryAtom,
  spotlightTypesAtom,
} from './atoms';
import { bestFuzzyRank } from './matching';
import {
  type TAction,
  type TActionRenderProp,
  type TCtx,
  type TGroup,
  type TModeFrame,
  type TModePath,
  type TModifiers,
} from './types';

// Live modifier state is NOT part of the view derivation — it changes on every
// Shift/Cmd press and nothing in the derived rows (hidden/label/order/render) reads
// it. Execute-time modifiers come from the keyboard/mouse event via runAction, and a
// future modifier-dependent hint should read useSpotlightModifiers() in its own row.
const NO_MODIFIERS: TModifiers = { cmd: false, ctrl: false, shift: false, opt: false };

/** One action resolved for rendering: its label, ctx, render fn, and React key. */
export type RenderableAction = {
  action: TAction;
  ctx: TCtx;
  label: string;
  render: TActionRenderProp;
  reactKey: string;
};

export type SpotlightGroupView = { group: TGroup; items: RenderableAction[] };

/** The fully-derived Spotlight view: grouped + sorted, plus a flat list for nav. */
export type SpotlightView = {
  groups: SpotlightGroupView[];
  flat: RenderableAction[];
  modePath: TModeFrame[];
  query: string;
  /** True when the sole result is an injected fallback (no normal action matched). */
  fallbackActive: boolean;
  /** The active fallback's leading input adornment, if any. */
  inputAdornment?: ReactNode;
};

const isDev = process.env.NODE_ENV === 'development';
const warned = new Set<string>();
const warnOnce = (message: string) => {
  if (!isDev || warned.has(message)) return;
  warned.add(message);
  console.warn(message);
};

const resolve = <T>(value: T | ((ctx: TCtx) => T), ctx: TCtx): T =>
  typeof value === 'function' ? (value as (ctx: TCtx) => T)(ctx) : value;

/** Decompose the current path (root → top), attaching each frame's registered mode data. */
function buildModePath(currentPath: TModePath, modeData: Record<TModePath, unknown>): TModeFrame[] {
  const frames: TModeFrame[] = [{ segment: null, data: modeData['/'] }];
  let acc = '';
  for (const segment of toSegments(currentPath)) {
    acc += `/${segment}`;
    frames.push({ segment, data: modeData[acc] });
  }
  return frames;
}

/**
 * Derive the visible, filtered, grouped, and sorted Spotlight view from the
 * registry atoms. Actions are filtered by `hidden`, prefix-match on the current
 * mode path, and a fuzzy match on label + keywords; groups are sorted by
 * `(deepestMode * 1000 + priority)` descending; items within a group by action
 * priority then fuzzy rank. Recomputes only when the underlying
 * registry/query/mode state changes.
 */
export function useSpotlightView(): SpotlightView {
  const actions = useAtomValue(spotlightActionsAtom);
  const types = useAtomValue(spotlightTypesAtom);
  const groups = useAtomValue(spotlightGroupsAtom);
  const modes = useAtomValue(spotlightModesAtom);
  const modeStack = useAtomValue(spotlightModeStackAtom);
  const modeData = useAtomValue(spotlightModeDataAtom);
  const query = useAtomValue(spotlightQueryAtom);

  return useMemo(() => {
    type Scored = RenderableAction & {
      rank: number;
      priority: number;
      groupKey: string;
      modeDepth: number;
    };

    const currentPath = modeStack[modeStack.length - 1]?.path || '/';
    // Match on the trimmed query so a trailing/leading space (or a whitespace-only
    // query) can't zero out matches while the fallback gate below still fires — both
    // now read the same trimmed value.
    const q = query.trim();
    const modePath = buildModePath(currentPath, modeData);
    const prefixes = new Set(prefixesOf(currentPath));

    // When the mode at the current top opts in, show only actions gated to exactly
    // the current path — hiding shallower/global actions.
    const exclusive = Boolean(modes[currentPath]?.exclusive);

    const scored: Scored[] = [];
    // Fallback actions are held out of normal matching; one is injected below only
    // when nothing else matched a non-blank query.
    const fallbackCandidates: Array<{ action: TAction; index: number }> = [];
    // `action.mode` is already validated at registration, so cache its segment depth
    // instead of re-splitting per action on every keystroke.
    const depthCache = new Map<string, number>();
    const depthOf = (mode: TModePath): number => {
      let depth = depthCache.get(mode);
      if (depth === undefined) {
        depth = toSegments(mode).length;
        depthCache.set(mode, depth);
      }
      return depth;
    };
    actions.forEach((action, index) => {
      const ctx: TCtx = { modePath, modifiers: NO_MODIFIERS, query, data: action.data };

      if (resolve(action.hidden || false, ctx)) return;
      if (!prefixes.has(action.mode)) return;
      if (exclusive && action.mode !== currentPath) return;

      if (action.fallback) {
        fallbackCandidates.push({ action, index });
        return;
      }

      const label = resolve(action.label, ctx);
      const rank = bestFuzzyRank(q, label, action.keywords);
      if (rank === null) return;

      const render =
        action.actionItemRender ?? types[action.type]?.actionItemRender ?? (() => label);
      if (!action.actionItemRender && !types[action.type]) {
        warnOnce(`[Spotlight] no renderer for type "${action.type}" — falling back to label`);
      }

      scored.push({
        action,
        ctx,
        label,
        render,
        // Keyed actions namespace by mode (a key is unique only within its mode, but
        // ancestor + submode can be visible together). Unkeyed fall back to group+index —
        // never the resolved label, which would churn the key (remount) as the query narrows.
        reactKey: action.key ? `k:${action.mode}:${action.key}` : `i:${action.group}:${index}`,
        rank,
        priority: action.priority || 0,
        groupKey: action.group,
        modeDepth: depthOf(action.mode),
      });
    });

    // Bucket by group.
    const buckets = new Map<string, Scored[]>();
    for (const item of scored) {
      const bucket = buckets.get(item.groupKey);
      if (bucket) bucket.push(item);
      else buckets.set(item.groupKey, [item]);
    }

    const groupViews: Array<SpotlightGroupView & { deepestMode: number; key: string }> = [];
    for (const [groupKey, items] of buckets) {
      const group = groups[groupKey] || { key: groupKey, label: groupKey, priority: 0 };
      if (!groups[groupKey])
        warnOnce(`[Spotlight] unregistered group "${groupKey}" — using key as label`);

      // Stable sort: priority (desc) → fuzzy rank (desc) → registration order.
      const sorted = items
        .map((item, i) => ({ item, i }))
        .sort((a, b) => b.item.priority - a.item.priority || b.item.rank - a.item.rank || a.i - b.i)
        .map(({ item }) => item);

      const deepestMode = items.reduce((max, item) => Math.max(max, item.modeDepth), 0);
      groupViews.push({
        group,
        items: sorted,
        deepestMode,
        key: groupKey,
      });
    }

    // A deeper active mode floats its groups above shallower/global ones; ties break
    // on group priority, then key for a stable order. Tuple compare (not a packed
    // `depth * 1000 + priority`) so a large priority can't bleed into the depth band.
    groupViews.sort(
      (a, b) =>
        b.deepestMode - a.deepestMode ||
        (b.group.priority || 0) - (a.group.priority || 0) ||
        a.key.localeCompare(b.key)
    );

    const finalGroups: SpotlightGroupView[] = groupViews.map(({ group, items }) => ({
      group,
      items,
    }));
    const flat = finalGroups.flatMap(view => view.items);

    // Nothing matched a non-blank query → offer the top fallback as the sole result,
    // so it inherits auto-focus + Enter from the normal pipeline.
    if (flat.length === 0 && q !== '' && fallbackCandidates.length) {
      const pick = [...fallbackCandidates].sort(
        (a, b) => (b.action.priority || 0) - (a.action.priority || 0) || a.index - b.index
      )[0].action;
      const ctx: TCtx = { modePath, modifiers: NO_MODIFIERS, query, data: pick.data };
      const label = resolve(pick.label, ctx);
      const render = pick.actionItemRender ?? types[pick.type]?.actionItemRender ?? (() => label);
      const item: RenderableAction = {
        action: pick,
        ctx,
        label,
        render,
        reactKey: pick.key ? `k:${pick.mode}:${pick.key}` : `fallback:${pick.group}`,
      };
      const group: TGroup = { key: pick.group, label: '' };
      return {
        groups: [{ group, items: [item] }],
        flat: [item],
        modePath,
        query,
        fallbackActive: true,
        inputAdornment: resolve(pick.inputAdornment, ctx),
      };
    }

    return { groups: finalGroups, flat, modePath, query, fallbackActive: false };
  }, [actions, types, groups, modes, modeStack, modeData, query]);
}
